import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleCheck, ExternalLink, EyeOff, Package, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { productsAPI } from './services/api';
import { Table, Modal, Popconfirm, Tabs, TagsInput, Form, Field, useForm, toast } from '../../ui';
import {
  PageHeader,
  StatCard,
  FilterBar,
  Thumb,
  LoadingState,
  ErrorState,
  MediaEditor,
  mediaRule,
  newMediaKey,
  KeyValueEditor,
  keyValueRule,
  objectToRows,
  rowsToObject,
} from './components';
import EmptyState from '../../components/site/EmptyState';
import { formatINR, formatDate } from '../../utils/format';
import { getErrorMessage } from '../../utils/apiError';
import { guessImageMime } from './utils/helpers';

const VISIBILITY_OPTIONS = ['Public', 'Private', 'Hidden'];
const VISIBILITY_TONE = { Public: 'success', Private: 'warning', Hidden: undefined };

const EMPTY_VALUES = {
  name: '',
  sortDescription: '',
  longDescription: '',
  price: null,
  category: '',
  visibility: 'Public',
  isActive: true,
  isTrending: false,
  isFeatured: false,
  media: [],
  features: [],
  technologies: [],
  whatInsideTheBox: [],
  keywords: [],
  tags: [],
  edition: '',
  contentDescription: '',
  estimatedDownloadSizeMB: null,
  additionalDetails: [],
};

/** Product from the API -> flat form values (digitalProductDetails and additionalDetails flattened). */
const toFormValues = product => ({
  name: product.name || '',
  sortDescription: product.sortDescription || '',
  longDescription: product.longDescription || '',
  price: product.price ?? null,
  category: product.category || '',
  visibility: VISIBILITY_OPTIONS.includes(product.visibility) ? product.visibility : 'Public',
  isActive: product.isActive ?? true,
  isTrending: Boolean(product.isTrending),
  isFeatured: Boolean(product.isFeatured),
  media: (product.media || [])
    .filter(m => m && m.url)
    .map(m => ({
      _key: newMediaKey(),
      title: m.title || '',
      url: m.url,
      mimeType: m.mimeType || '',
      fileSizeInBytes: m.fileSizeInBytes || 0,
    })),
  features: product.features || [],
  technologies: product.technologies || [],
  whatInsideTheBox: product.whatInsideTheBox || [],
  keywords: product.keywords || [],
  tags: product.tags || [],
  edition: product.digitalProductDetails?.edition || '',
  contentDescription: product.digitalProductDetails?.contentDescription || '',
  estimatedDownloadSizeMB: product.digitalProductDetails?.estimatedDownloadSizeMB ?? null,
  additionalDetails: objectToRows(product.additionalDetails),
});

/** Flat form values -> the POST/PUT /Products body. */
const toPayload = values => ({
  name: (values.name || '').trim(),
  sortDescription: (values.sortDescription || '').trim(),
  longDescription: (values.longDescription || '').trim(),
  price: Number(values.price),
  category: values.category,
  visibility: values.visibility || 'Public',
  productType: 'Digital',
  isActive: Boolean(values.isActive),
  isTrending: Boolean(values.isTrending),
  isFeatured: Boolean(values.isFeatured),
  media: (values.media || [])
    .filter(m => (m.url || '').trim())
    .map(m => ({
      title: (m.title || '').trim(),
      url: m.url.trim(),
      type: 'image',
      mimeType: m.mimeType || guessImageMime(m.url),
      fileSizeInBytes: Number(m.fileSizeInBytes) || 0,
    })),
  features: values.features || [],
  technologies: values.technologies || [],
  whatInsideTheBox: values.whatInsideTheBox || [],
  keywords: values.keywords || [],
  tags: values.tags || [],
  digitalProductDetails: {
    edition: (values.edition || '').trim(),
    contentDescription: (values.contentDescription || '').trim(),
    estimatedDownloadSizeMB: Math.max(0, Math.round(Number(values.estimatedDownloadSizeMB) || 0)),
  },
  additionalDetails: rowsToObject(values.additionalDetails),
});

const productPath = product => `/products/${product.id}/${encodeURIComponent(product.name || 'project')}`;

// Which tab holds each field, so a validation error on a hidden tab can be shown.
const FIELD_TAB = {
  media: 'images',
  features: 'content',
  technologies: 'content',
  whatInsideTheBox: 'content',
  keywords: 'content',
  tags: 'content',
  edition: 'download',
  contentDescription: 'download',
  estimatedDownloadSizeMB: 'download',
  additionalDetails: 'extra',
};

const tagsField = (name, label, placeholder, help) => (
  <Field name={name} label={label} help={help}>
    <TagsInput placeholder={placeholder} tokenSeparators={[]} />
  </Field>
);

/** The add/edit form. Mounted fresh each time the modal opens, so it never shows stale values. */
const ProductForm = ({ product, categories, onCancel, onSaved }) => {
  const form = useForm();
  const [tab, setTab] = useState('details');
  const [saving, setSaving] = useState(false);
  const initialValues = useMemo(() => (product ? toFormValues(product) : EMPTY_VALUES), [product]);

  const categoryOptions = useMemo(() => {
    const names = categories.map(c => c.name);
    if (product?.category && !names.includes(product.category)) names.push(product.category);
    return names;
  }, [categories, product]);

  const handleFinish = async () => {
    // Tabs only mount the panels that were opened, so read every value from the store,
    // over the product being edited, instead of only the fields on screen.
    const values = { ...initialValues, ...form.getFieldsValue(true) };
    const payload = toPayload(values);

    setSaving(true);
    try {
      if (product) {
        await productsAPI.update(product.id, payload);
        toast.success('Product updated');
      } else {
        await productsAPI.create(payload);
        toast.success('Product created');
      }
      onSaved();
    } catch (error) {
      if (product && error?.response?.status === 404) {
        toast.error('This product no longer exists. The list has been refreshed.');
        onSaved();
      } else {
        toast.error(getErrorMessage(error, "Couldn't save the product. Please try again."));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFinishFailed = ({ errorFields }) => {
    const first = errorFields?.[0]?.name?.[0];
    const target = FIELD_TAB[first] || 'details';
    if (target !== tab) {
      setTab(target);
      requestAnimationFrame(() => document.querySelector('.adm-modal [aria-invalid="true"]')?.focus());
    }
  };

  const items = [
    {
      key: 'details',
      label: 'Details',
      children: (
        <>
          <Field
            name="name"
            label="Name"
            rules={[
              { required: true, whitespace: true, message: 'Enter a product name' },
              { min: 3, message: 'Use at least 3 characters' },
            ]}
          >
            <input className="pk-input" placeholder="e.g. Hospital Management System (MERN)" maxLength={150} />
          </Field>

          <Field
            name="sortDescription"
            label="Short description"
            rules={[
              { required: true, whitespace: true, message: 'Enter a short description' },
              { max: 200, message: 'Keep it under 200 characters' },
            ]}
          >
            {field => (
              <>
                <textarea {...field} className="pk-textarea" rows={2} maxLength={200} style={{ minHeight: 72 }} />
                <div className="pk-hint" style={{ textAlign: 'right' }}>
                  {(field.value || '').length}/200
                </div>
              </>
            )}
          </Field>

          <Field name="longDescription" label="Full description" help="Shown on the product page. Line breaks are kept.">
            <textarea className="pk-textarea" rows={6} />
          </Field>

          <div className="pk-form__row">
            <Field
              name="price"
              label="Price (₹)"
              rules={[
                { required: true, message: 'Enter a price' },
                { type: 'number', min: 1, message: 'Price must be at least ₹1' },
              ]}
            >
              <input type="number" className="pk-input" min={1} step={1} inputMode="decimal" placeholder="1499" />
            </Field>

            <Field name="category" label="Category" rules={[{ required: true, message: 'Choose a category' }]}>
              <select className="pk-select">
                <option value="" disabled>
                  Choose a category
                </option>
                {categoryOptions.map(name => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="pk-form__row">
            <Field name="visibility" label="Visibility">
              <select className="pk-select">
                {VISIBILITY_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </Field>
            <div className="form-item">
              <span className="form-label">Product type</span>
              <input className="pk-input" value="Digital" disabled aria-label="Product type" />
            </div>
          </div>

          <div className="adm-checks">
            {[
              ['isActive', 'Active'],
              ['isTrending', 'Trending'],
              ['isFeatured', 'Featured'],
            ].map(([name, label]) => (
              <Field key={name} name={name} valuePropName="checked" noStyle>
                {field => (
                  <label className="pk-check">
                    <input type="checkbox" {...field} />
                    <span>{label}</span>
                  </label>
                )}
              </Field>
            ))}
          </div>
        </>
      ),
    },
    {
      key: 'images',
      label: 'Images',
      children: (
        <Field
          name="media"
          label="Images"
          rules={[mediaRule]}
          help="Paste an image URL (e.g. from your CDN or GitHub). The first image is the cover; use the arrows to reorder."
        >
          {field => <MediaEditor id={field.id} value={field.value} onChange={field.onChange} />}
        </Field>
      ),
    },
    {
      key: 'content',
      label: 'Content',
      children: (
        <>
          {tagsField('features', 'Features', 'Type a feature and press Enter')}
          {tagsField('technologies', 'Technologies', 'Type a technology and press Enter', 'Any technology; press Enter after each one.')}
          {tagsField('whatInsideTheBox', "What's included", 'Type an item and press Enter')}
          <div className="pk-form__row">
            {tagsField('keywords', 'Search keywords', 'Press Enter after each')}
            {tagsField('tags', 'Tags', 'Press Enter after each')}
          </div>
        </>
      ),
    },
    {
      key: 'download',
      label: 'Download',
      children: (
        <>
          <div className="pk-form__row">
            <Field name="edition" label="Edition">
              <input className="pk-input" placeholder="Standard" maxLength={60} />
            </Field>
            <Field
              name="estimatedDownloadSizeMB"
              label="Download size (MB)"
              rules={[{ type: 'integer', min: 0, message: 'Use a whole number of MB (0 or more)' }]}
            >
              <input type="number" className="pk-input" min={0} step={1} inputMode="numeric" placeholder="80" />
            </Field>
          </div>
          <Field name="contentDescription" label="What the download contains">
            <textarea className="pk-textarea" rows={4} placeholder="Source code, documentation, walkthrough video…" />
          </Field>
        </>
      ),
    },
    {
      key: 'extra',
      label: 'Extra details',
      children: (
        <Field
          name="additionalDetails"
          label="Extra details"
          rules={[keyValueRule]}
          help="Shown as name / value pairs on the product page, e.g. Difficulty: Beginner."
        >
          {field => (
            <KeyValueEditor
              id={field.id}
              value={field.value}
              onChange={field.onChange}
              keyPlaceholder="Name (e.g. Difficulty)"
              valuePlaceholder="Value (e.g. Beginner)"
            />
          )}
        </Field>
      ),
    },
  ];

  return (
    <Form form={form} initialValues={initialValues} onFinish={handleFinish} onFinishFailed={handleFinishFailed}>
      <Tabs items={items} activeKey={tab} onChange={setTab} />
      <div className="adm-form-foot">
        <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="pk-btn pk-btn--primary pk-btn--sm"
          disabled={saving}
          aria-busy={saving || undefined}
        >
          {product ? 'Save changes' : 'Create product'}
        </button>
      </div>
    </Form>
  );
};

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const loadProducts = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [list, cats] = await Promise.all([
        productsAPI.getAllForAdmin(),
        productsAPI.getCategories().catch(() => null),
      ]);
      setProducts(list);
      if (cats) setCategories(cats);
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't load products."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const setBusy = (id, busy) =>
    setBusyIds(prev => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = product => {
    setEditing(product);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSaved = () => {
    setModalOpen(false);
    loadProducts({ quiet: true });
  };

  const toggleActive = async product => {
    const next = !product.isActive;
    setBusy(product.id, true);
    try {
      await productsAPI.update(product.id, toPayload({ ...toFormValues(product), isActive: next }));
      setProducts(list => list.map(p => (p.id === product.id ? { ...p, isActive: next } : p)));
      toast.success(next ? `"${product.name}" is active` : `"${product.name}" is now inactive`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't change the status."));
    } finally {
      setBusy(product.id, false);
    }
  };

  const handleDelete = async product => {
    try {
      await productsAPI.delete(product.id);
      setProducts(list => list.filter(p => p.id !== product.id));
      toast.success('Product deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't delete the product."));
    }
  };

  const categoryNames = useMemo(() => {
    const names = new Set(categories.map(c => c.name));
    products.forEach(p => p.category && names.add(p.category));
    return [...names];
  }, [categories, products]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter(p => {
      if (categoryFilter && p.category !== categoryFilter) return false;
      if (statusFilter === 'active' && !p.isActive) return false;
      if (statusFilter === 'inactive' && p.isActive) return false;
      if (visibilityFilter && (p.visibility || 'Public') !== visibilityFilter) return false;
      if (!term) return true;
      return [p.name, p.productId, p.category, ...(p.tags || [])].some(v =>
        String(v || '').toLowerCase().includes(term)
      );
    });
  }, [products, search, categoryFilter, statusFilter, visibilityFilter]);

  const stats = useMemo(
    () => ({
      total: products.length,
      active: products.filter(p => p.isActive).length,
      notPublic: products.filter(p => (p.visibility || 'Public') !== 'Public').length,
      featured: products.filter(p => p.isFeatured).length,
    }),
    [products]
  );

  const columns = [
    {
      title: 'Product',
      key: 'product',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (_, record) => (
        <div className="adm-cell">
          <Thumb src={record.media?.[0]?.url} />
          <div className="adm-cell__text">
            <div className="adm-cell-title">{record.name}</div>
            {record.productId && <div className="adm-cell-sub">{record.productId}</div>}
          </div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      render: category => (category ? <span className="adm-tag">{category}</span> : <span className="pk-muted">—</span>),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 110,
      align: 'right',
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
      render: price => <span className="adm-num">{formatINR(price)}</span>,
    },
    {
      title: 'Visibility',
      dataIndex: 'visibility',
      key: 'visibility',
      width: 120,
      render: visibility => {
        const value = visibility || 'Public';
        return (
          <span className="pk-badge" data-tone={VISIBILITY_TONE[value]}>
            {value}
          </span>
        );
      },
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 130,
      render: (isActive, record) => (
        <label className="adm-switch">
          <input
            type="checkbox"
            role="switch"
            className="switch switch-sm"
            checked={Boolean(isActive)}
            disabled={busyIds.has(record.id)}
            aria-label={`Active: ${record.name}`}
            onChange={() => toggleActive(record)}
          />
          <span>{isActive ? 'Active' : 'Inactive'}</span>
        </label>
      ),
    },
    {
      title: 'Updated',
      key: 'updated',
      width: 120,
      sorter: (a, b) =>
        new Date(a.updatedDate || a.createdDate || 0) - new Date(b.updatedDate || b.createdDate || 0),
      render: (_, record) => <span className="adm-nowrap">{formatDate(record.updatedDate || record.createdDate)}</span>,
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <div className="adm-actions">
          <Link
            to={productPath(record)}
            target="_blank"
            rel="noopener noreferrer"
            className="adm-icon-btn"
            aria-label={`View ${record.name} in the store (opens in a new tab)`}
            title="View in store"
          >
            <ExternalLink aria-hidden="true" />
          </Link>
          <button
            type="button"
            className="adm-icon-btn"
            aria-label={`Edit ${record.name}`}
            title="Edit"
            onClick={() => openEdit(record)}
          >
            <Pencil aria-hidden="true" />
          </button>
          <Popconfirm
            title="Delete this product?"
            description="This can't be undone. To take it off sale instead, switch Active off."
            okText="Delete"
            okDanger
            onConfirm={() => handleDelete(record)}
          >
            <button
              type="button"
              className="adm-icon-btn"
              data-tone="danger"
              aria-label={`Delete ${record.name}`}
              title="Delete"
            >
              <Trash2 aria-hidden="true" />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const hasFilters = Boolean(search || categoryFilter || statusFilter || visibilityFilter);

  let body;
  if (loading) {
    body = <LoadingState label="Loading products" />;
  } else if (error) {
    body = (
      <div className="adm-panel__state">
        <ErrorState title="Couldn't load products" message={error} onRetry={() => loadProducts()} />
      </div>
    );
  } else if (products.length === 0) {
    body = (
      <div className="adm-panel__state">
        <EmptyState
          icon={<Package />}
          title="No products yet"
          text="Add your first project to start selling."
          actions={
            <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={openCreate}>
              <Plus aria-hidden="true" />
              Add product
            </button>
          }
        />
      </div>
    );
  } else {
    body = (
      <>
        <FilterBar
          inline
          searchLabel="Search products"
          searchPlaceholder="Search name, code, category or tag"
          searchValue={search}
          onSearchChange={setSearch}
          filters={[
            {
              key: 'category',
              component: (
                <select
                  className="pk-select"
                  aria-label="Filter by category"
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                >
                  <option value="">All categories</option>
                  {categoryNames.map(name => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              ),
            },
            {
              key: 'status',
              component: (
                <select
                  className="pk-select"
                  aria-label="Filter by status"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="">Active and inactive</option>
                  <option value="active">Active only</option>
                  <option value="inactive">Inactive only</option>
                </select>
              ),
            },
            {
              key: 'visibility',
              component: (
                <select
                  className="pk-select"
                  aria-label="Filter by visibility"
                  value={visibilityFilter}
                  onChange={e => setVisibilityFilter(e.target.value)}
                >
                  <option value="">Any visibility</option>
                  {VISIBILITY_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ),
            },
          ]}
          onClearFilters={
            hasFilters
              ? () => {
                  setSearch('');
                  setCategoryFilter('');
                  setStatusFilter('');
                  setVisibilityFilter('');
                }
              : undefined
          }
          end={<span className="adm-toolbar__count">{filtered.length} shown</span>}
        />
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          scroll={{ x: 980 }}
          emptyText="No products match these filters."
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
        />
      </>
    );
  }

  return (
    <div>
      <PageHeader
        title="Products"
        description="Everything in the catalog, including inactive and hidden products."
        actions={
          <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={openCreate}>
            <Plus aria-hidden="true" />
            Add product
          </button>
        }
      />

      {!error && (
        <div className="adm-stats">
          <StatCard title="Products" value={stats.total} icon={<Package />} loading={loading} />
          <StatCard title="Active" value={stats.active} icon={<CircleCheck />} loading={loading} />
          <StatCard title="Not public" value={stats.notPublic} icon={<EyeOff />} hint="Private or hidden" loading={loading} />
          <StatCard title="Featured" value={stats.featured} icon={<Star />} loading={loading} />
        </div>
      )}

      <section className="adm-panel" aria-label="Products">
        {body}
      </section>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editing ? `Edit ${editing.name}` : 'Add product'}
        footer={null}
        width={880}
        className="adm-modal"
        maskClosable={false}
      >
        <ProductForm product={editing} categories={categories} onCancel={closeModal} onSaved={handleSaved} />
      </Modal>
    </div>
  );
};

export default ProductManagement;
